import { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { ParamValue, ParamValues } from './useFilter.types';
import { appendIfValidFilterValues, arg2Order, orderToArg, toValue } from './useFilter.utils';

type Formatter = (values: any) => any;

export type FilterHook = {
  initialValues: ParamValues;
  addFilters: (filtersToAdd: any) => void;
  removeFilter: (filterField: string, value?: any) => void;
  onClear: () => void;
  onSubmit: (values: any) => void;
  isSubmitting: boolean;
};

export type FilterHookOptions = {
  toArgFormatter?: Formatter;
  toValuesFormatter?: Formatter;
};

export const buildSearchParamsFromValues = (values: ParamValues, formatter?: Formatter) => {
  const newParams = new URLSearchParams();
  if (!values) {
    return newParams;
  }
  const paramsToAdd = formatter ? formatter(values) : values;

  const paramsToAddKeys = Object.keys(paramsToAdd);

  for (let index = 0; index < paramsToAddKeys.length; index += 1) {
    const key = paramsToAddKeys[index];

    const value = key === 'order' ? orderToArg(paramsToAdd[key]) : paramsToAdd[key];

    if (Array.isArray(value)) {
      value.forEach((val) => appendIfValidFilterValues(newParams, key, val));
    } else {
      appendIfValidFilterValues(newParams, key, value);
    }
  }

  newParams.sort();
  return newParams;
};

export const recoverValuesFromSearchParams = (
  search?: string,
  formatter?: Formatter,
): ParamValues => {
  if (!search) {
    return {};
  }
  const searchParams = new URLSearchParams(search);

  const allKeysUnique = Array.from(new Set<string>(searchParams.keys()).values());
  const valuesObjects: { key: string; value: ParamValue }[] = allKeysUnique.map((key: string) => {
    const allValuesFromKey = searchParams.getAll(key);
    const verifiedValue = allValuesFromKey.length === 1 ? allValuesFromKey[0] : allValuesFromKey;
    return { key, value: verifiedValue };
  });

  const recoveredValues: Record<string, ParamValue> = {};

  valuesObjects.forEach((keyValueObject) => {
    switch (keyValueObject.key) {
      case 'order':
        recoveredValues[keyValueObject.key] = arg2Order(keyValueObject.value);
        break;
      default:
        recoveredValues[keyValueObject.key] = toValue(keyValueObject.value);
        break;
    }
  });

  return formatter ? formatter(recoveredValues) : recoveredValues;
};

const useFilter = <T = ParamValues>(defaultValues?: T, options?: FilterHookOptions): FilterHook => {
  const history = useHistory();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const applyValuesToUrl = useCallback(
    (values: any) => {
      const search = `?${buildSearchParamsFromValues(values, options?.toArgFormatter)}`;
      if (history.location.search === search) {
        return;
      }
      const newUrl = `${history.location.pathname}${search}${history.location.hash}`;
      history.replace(newUrl, history.location.state);
    },
    [history, options?.toArgFormatter],
  );

  const onSubmit = (values: any) => {
    setIsSubmitting(true);
    applyValuesToUrl(values);
    setTimeout(() => {
      setIsSubmitting(false);
    }, 1500);
  };

  const addFilters = useCallback(
    (filtersToAdd: any) => {
      const currentFilters = recoverValuesFromSearchParams(history.location.search);
      const allFilters = { ...currentFilters, ...filtersToAdd };
      applyValuesToUrl(allFilters);
    },
    [applyValuesToUrl, history.location.search],
  );

  const removeFilter = (filterField: string, value?: any) => {
    const currentFilters = recoverValuesFromSearchParams(history.location.search);
    const filterValues = currentFilters[filterField];
    const newFilters = { ...currentFilters };

    if (Array.isArray(filterValues) && value) {
      const newValues = filterValues.filter((filterValue) => filterValue !== value);
      newFilters[filterField] = newValues;
    } else {
      delete newFilters[filterField];
    }

    applyValuesToUrl(newFilters);
  };

  const onClear = () => {
    applyValuesToUrl(defaultValues);
  };

  useEffect(() => {
    let isMounted = true;

    if (defaultValues && isMounted) {
      addFilters(defaultValues);
    }

    return () => {
      isMounted = false;
    };
  }, []);

  return {
    initialValues: recoverValuesFromSearchParams(
      history.location.search,
      options?.toValuesFormatter,
    ),
    addFilters,
    removeFilter,
    onClear,
    onSubmit,
    isSubmitting,
  };
};

export default useFilter;
